/**
 * <infinite-scroll-collection>
 *
 * Reemplaza la paginación clásica de una colección por scroll infinito.
 *
 * Cómo funciona:
 * 1. Cuando el elemento entra al DOM, busca el grid de productos, el
 *    "sentinel" (marcador invisible al final del grid) y el indicador de carga.
 * 2. Lee data-next-url (viene de {{ paginate.next.url }}). Si no hay página
 *    siguiente, no hace nada más.
 * 3. Observa el sentinel con IntersectionObserver. Cuando el usuario se
 *    acerca al final del grid, hace fetch() de la página siguiente completa
 *    (misma URL de la colección, con ?page=N y los filtros/orden que ya
 *    traiga la querystring).
 * 4. Del HTML devuelto, extrae solo los productos nuevos
 *    ([data-infinite-scroll-grid] de esa respuesta) y los añade al grid actual.
 * 5. Lee el nuevo data-next-url del HTML devuelto para saber si hay que
 *    seguir observando o si ya se llegó a la última página.
 * 6. Oculta el bloque de paginación clásica (fallback para SEO / no-JS),
 *    que sigue existiendo en el DOM con enlaces reales.
 *
 * Se dispara un evento `infinite-scroll:products-appended` en `document`
 * cada vez que se añaden productos nuevos, con { detail: { container } }.
 * Engánchate a ese evento si tu tema necesita reinicializar swatches,
 * quick-add, animaciones de entrada, etc. sobre los nodos nuevos.
 */
(function () {
  if (customElements.get('infinite-scroll-collection')) return;

  class InfiniteScrollCollection extends HTMLElement {
    constructor() {
      super();
      this.loading = false;
      this.observer = null;
      this.nextUrl = '';
    }

    connectedCallback() {
      this.grid = this.querySelector('[data-infinite-scroll-grid]');
      this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
      this.loadingIndicator = this.querySelector('[data-infinite-scroll-loading]');

      if (!this.grid || !this.sentinel) return;

      // El scroll infinito está activo: oculta la paginación clásica (queda
      // en el DOM para navegadores sin JS y para rastreadores).
      this.hidePaginationFallback();

      this.nextUrl = this.getAttribute('data-next-url') || '';

      if (!this.nextUrl) {
        this.sentinel.remove();
        return;
      }

      if (!('IntersectionObserver' in window)) {
        // Sin soporte de IntersectionObserver: no rompemos nada, simplemente
        // no se activa el scroll infinito y queda visible la paginación real.
        this.showPaginationFallback();
        return;
      }

      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), {
        root: null,
        // Empieza a cargar un poco antes de que el sentinel sea visible,
        // para que el usuario no vea un salto ni un hueco en blanco.
        rootMargin: '600px 0px 600px 0px',
        threshold: 0,
      });
      this.observer.observe(this.sentinel);
    }

    disconnectedCallback() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    hidePaginationFallback() {
      document.querySelectorAll('[data-infinite-scroll-pagination-fallback]').forEach((el) => {
        el.hidden = true;
      });
    }

    showPaginationFallback() {
      document.querySelectorAll('[data-infinite-scroll-pagination-fallback]').forEach((el) => {
        el.hidden = false;
      });
    }

    handleIntersect(entries) {
      const entry = entries[0];
      if (entry && entry.isIntersecting && !this.loading && this.nextUrl) {
        this.loadNextPage();
      }
    }

    async loadNextPage() {
      this.loading = true;
      if (this.loadingIndicator) this.loadingIndicator.hidden = false;

      try {
        const response = await fetch(this.nextUrl, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) {
          throw new Error('Infinite scroll: respuesta no OK (' + response.status + ')');
        }

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        const newGrid = doc.querySelector('[data-infinite-scroll-grid]');
        const newRoot = doc.querySelector('infinite-scroll-collection');

        if (newGrid && newGrid.children.length) {
          const fragment = document.createDocumentFragment();
          Array.from(newGrid.children).forEach((child) => {
            fragment.appendChild(child);
          });
          this.grid.appendChild(fragment);

          document.dispatchEvent(
            new CustomEvent('infinite-scroll:products-appended', {
              detail: { container: this.grid },
            })
          );
        }

        this.nextUrl = newRoot ? newRoot.getAttribute('data-next-url') || '' : '';

        if (!this.nextUrl) {
          if (this.observer) this.observer.disconnect();
          this.sentinel.remove();
        }
      } catch (error) {
        // Si algo falla (red, parseo, etc.) dejamos de intentar cargar más
        // por scroll y mostramos la paginación real como red de seguridad.
        console.error('[infinite-scroll-collection]', error);
        if (this.observer) this.observer.disconnect();
        this.showPaginationFallback();
      } finally {
        this.loading = false;
        if (this.loadingIndicator) this.loadingIndicator.hidden = true;
      }
    }
  }

  customElements.define('infinite-scroll-collection', InfiniteScrollCollection);
})();